import Raven from 'raven-js';
import { useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { Link as TLink } from 'foundations-components/transitional/components/Link';
import { View } from 'foundations-components/transitional/components/View';
import ACSkeletonBox from 'academy-common-lib/components/loading/ACSkeletonBox';
import { useSyncStaticStateInsideIFrame } from 'embedded-academy-lib/insideFrame';
import { useGetNewLessonContentMappingByOldMappingAndLanguageQuery } from '../../../../__generated__/chirp/com/hubspot/academy/catalog/rpc/MigrationChirpService.hooks';
import { Error } from '../../error/components/Error';
import { ErrorBoundary } from '../../error/components/ErrorBoundary';
import { Metrics } from '../../../Metrics';
import { SkeletonGroup } from '../../dynamicContentWidget/components/SkeletonGroup';
import { useStaticContentState } from '../staticContentWidgetHooks';
import { getAcademyRedirectUrl, getStaticLessonRedirectUrl, getStaticShortsRedirectUrl } from '../urlUtils';
import { WidgetWrapper } from '../../layout/components/WidgetWrapper';
import { Video } from '../../video/components/Video';
import { VideoSection } from '../../video/components/VideoSection';
import { useLesson, useShorts } from '../../../hooks/contentHooks';
// @ts-expect-error untyped module
import { useParentAppName } from '../../../hooks/searchParamHooks';
import { usageTracker } from '../../../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StaticContentLoading = ({
  variant = 'default'
}) => /*#__PURE__*/_jsx(NavMarker, {
  name: "STATIC_WIDGET_LOADING",
  children: /*#__PURE__*/_jsxs(WidgetWrapper, {
    children: [/*#__PURE__*/_jsx(View, {
      "data-test-id": "static-content-loading",
      marginBlockEnd: "200",
      children: /*#__PURE__*/_jsx(ACSkeletonBox, {
        animated: true,
        style: {
          width: '100%',
          aspectRatio: '16/9'
        }
      })
    }), variant === 'default' && /*#__PURE__*/_jsx(SkeletonGroup, {
      marginBlockStart: "800",
      rows: 5,
      variableWidth: true
    })]
  })
});
const StaticContentNotFound = ({
  status
}) => {
  const {
    onNoContent
  } = useSyncStaticStateInsideIFrame();
  const {
    lessonId,
    contentId,
    shortId,
    variant,
    authType,
    openLinksInNewTab
  } = useStaticContentState();
  const parentAppName = useParentAppName();
  const currentVariant = variant || 'default';
  const widgetType = authType || 'app';
  useEffect(() => {
    usageTracker.track('pageView', Object.assign({
      screen: `static-content-widget-${widgetType}-${currentVariant}-not-found`,
      contentType: lessonId ? 'LESSON' : 'SHORT_FORM_CONTENT'
    }, contentId && {
      contentId
    }, lessonId && {
      lessonId
    }, shortId && {
      shortId
    }));
    Metrics.counter('static-content-widget-not-found', {
      variant: currentVariant,
      authType: widgetType,
      hostAppName: parentAppName,
      contentType: lessonId ? 'LESSON' : 'SHORT_FORM_CONTENT',
      contentId: String(lessonId || shortId || '')
    }).increment();
    onNoContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId, lessonId, onNoContent, shortId, variant]);
  if (status === 403) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "STATIC_RESULTS_FORBIDDEN",
      children: /*#__PURE__*/_jsx(WidgetWrapper, {
        children: /*#__PURE__*/_jsx(ErrorMessage, {
          illustration: "lock",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyCommon.errors.forbidden.heading"
          }),
          type: "403",
          children: /*#__PURE__*/_jsx(TLink, {
            "data-test-id": "embedded-academy-not-found-redirect-link",
            external: openLinksInNewTab !== null && openLinksInNewTab !== void 0 ? openLinksInNewTab : true,
            href: getAcademyRedirectUrl(),
            target: openLinksInNewTab !== false ? '_blank' : '_top',
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "staticWidget.notFound.link"
            })
          })
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "STATIC_RESULTS_NOT_FOUND",
    children: /*#__PURE__*/_jsx(WidgetWrapper, {
      children: /*#__PURE__*/_jsx(ErrorMessage, {
        illustration: "errors/map",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "staticWidget.notFound.title"
        }),
        type: "notFound",
        children: /*#__PURE__*/_jsx(TLink, {
          "data-test-id": "embedded-academy-not-found-redirect-link",
          external: openLinksInNewTab !== null && openLinksInNewTab !== void 0 ? openLinksInNewTab : true,
          href: getAcademyRedirectUrl(),
          target: openLinksInNewTab !== false ? '_blank' : '_top',
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "staticWidget.notFound.link"
          })
        })
      })
    })
  });
};
const StaticContentError = ({
  error
}) => {
  const {
    onError
  } = useSyncStaticStateInsideIFrame();
  const {
    authType
  } = useStaticContentState();
  const parentAppName = useParentAppName();
  const errorStatus = (error === null || error === void 0 ? void 0 : error.status) || 500;
  const widgetType = authType || 'app';
  useEffect(() => {
    if (![404, 403].includes(errorStatus)) {
      Metrics.counter('static-content-error', {
        hostAppName: parentAppName,
        authType: widgetType
      }).increment();
      Raven.captureException(error);
      onError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onError, error, errorStatus]);
  const renderError = () => {
    switch (errorStatus) {
      case 404:
      case 403:
        return /*#__PURE__*/_jsx(StaticContentNotFound, {
          status: errorStatus
        });
      default:
        return /*#__PURE__*/_jsx(Error, {});
    }
  };
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    marker: "STATIC_RESULTS_PAGE_ERROR",
    children: /*#__PURE__*/_jsx(WidgetWrapper, {
      children: renderError()
    })
  });
};
export const StaticContentResult = ({
  isResizeReady = true
}) => {
  var _migrationData$value, _migrationData$value2, _lesson$content, _content$hubId;
  const {
    authType = 'app',
    autoPlay,
    lessonId,
    contentId,
    shortId,
    language,
    mutedByDefault,
    variant,
    startTime,
    sectionEndTime,
    openLinksInNewTab,
    renderContext,
    enablePictureInPicture,
    hublet,
    linkTextProps,
    videoWrapperProps
  } = useStaticContentState();
  const {
    onContent,
    onVideoFinish,
    onVideoPlay,
    onVideoEngagement,
    onVideoProgress,
    onWatermarkClick
  } = useSyncStaticStateInsideIFrame();
  const isLessonContent = Boolean(lessonId && contentId);
  const isPublic = authType === 'public';
  const {
    data: migrationData,
    loading: migrationLoading
  } = useGetNewLessonContentMappingByOldMappingAndLanguageQuery({
    variables: {
      oldLessonId: Number(lessonId),
      oldContentId: Number(contentId),
      language: (language === null || language === void 0 ? void 0 : language.toUpperCase()) || 'EN'
    },
    skip: !isLessonContent || !lessonId || !contentId || !language
  });
  const resolvedLessonId = (migrationData === null || migrationData === void 0 || (_migrationData$value = migrationData.value) === null || _migrationData$value === void 0 ? void 0 : _migrationData$value.newLessonId) || Number(lessonId);
  const resolvedContentId = (migrationData === null || migrationData === void 0 || (_migrationData$value2 = migrationData.value) === null || _migrationData$value2 === void 0 ? void 0 : _migrationData$value2.newContentId) || Number(contentId);
  const {
    lesson,
    lessonLoading,
    lessonError
  } = useLesson(resolvedLessonId, isPublic);
  const {
    shortFormContent,
    shortFormContentError,
    shortFormContentLoading
  } = useShorts(shortId, isPublic);
  const lessonContent = lesson === null || lesson === void 0 || (_lesson$content = lesson.content) === null || _lesson$content === void 0 ? void 0 : _lesson$content.find(c => c.id === Number(resolvedContentId));
  const content = lessonContent || shortFormContent;
  const videoStartTime = startTime ? Number(startTime) : undefined;
  const videoEndTime = sectionEndTime ? Number(sectionEndTime) : undefined;
  const clipRange = videoStartTime !== undefined && videoEndTime !== undefined ? {
    startTime: videoStartTime,
    endTime: videoEndTime
  } : undefined;
  const creatorHubId = (_content$hubId = content === null || content === void 0 ? void 0 : content.hubId) !== null && _content$hubId !== void 0 ? _content$hubId : undefined;
  const creatorHublet = hublet || (content === null || content === void 0 ? void 0 : content.hublet) || undefined;
  const videoType = content && 'type' in content ? content.type : undefined;
  const redirectLink = isLessonContent && lesson ? getStaticLessonRedirectUrl(resolvedLessonId, resolvedContentId, language, videoStartTime) : getStaticShortsRedirectUrl(shortId, videoStartTime);
  const contentLanguage = content !== null && content !== void 0 && content.language ? readUnhandledEnumCase(content.language).toLowerCase() : 'en';
  useEffect(() => {
    if (isLessonContent && content) {
      onContent({
        contentName: content.name,
        contentType: 'LESSON',
        contentId: resolvedContentId,
        lessonId: resolvedLessonId,
        language: contentLanguage,
        sourceUrl: redirectLink
      });
    }
    if (!isLessonContent && content) {
      onContent({
        contentName: content.name,
        contentDescription: content.description,
        shortId: shortId,
        contentType: 'SHORT_FORM_CONTENT',
        language: contentLanguage,
        sourceUrl: redirectLink
      });
    }
    if (content) {
      const currentVariant = variant || 'default';
      const widgetType = (authType || 'app').toLowerCase();
      usageTracker.track('pageView', Object.assign({
        screen: `static-content-widget-${widgetType}-${currentVariant}`,
        contentType: isLessonContent ? 'LESSON' : 'SHORT_FORM_CONTENT'
      }, resolvedContentId && {
        contentId: resolvedContentId
      }, resolvedLessonId && {
        lessonId: resolvedLessonId
      }, shortId && {
        shortId
      }));
      Metrics.counter('static-content-widget-success', {
        variant: currentVariant,
        authType: widgetType
      }).increment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onContent, isLessonContent, content, resolvedContentId, resolvedLessonId, shortId]);

  // To handle the case where error page is shown until lesson or short id is available from state
  if (!lessonId && !shortId) return null;

  // Handle the not found state if the content is missing and there is a lesson. For shorts, this case is handled in StaticContentError
  if (!lessonContent && lesson) {
    return /*#__PURE__*/_jsx(StaticContentNotFound, {});
  }
  if (lessonLoading || shortFormContentLoading) {
    return /*#__PURE__*/_jsx(StaticContentLoading, {
      variant: isResizeReady ? variant : 'video'
    });
  }
  const contentLoadingError = lessonError || shortFormContentError || !content;
  // When a lesson is not found, wait until we check for a migrated lesson
  if (contentLoadingError && migrationLoading) {
    return /*#__PURE__*/_jsx(StaticContentLoading, {
      variant: isResizeReady ? variant : 'video'
    });
  }
  if (contentLoadingError) {
    return /*#__PURE__*/_jsx(StaticContentError, {
      error: lessonError || shortFormContentError
    });
  }
  if (variant === 'video') {
    return /*#__PURE__*/_jsx(ErrorBoundary, {
      marker: "STATIC_RESULTS_PAGE_ERROR",
      children: /*#__PURE__*/_jsx(NavMarker, {
        name: "STATIC_VIDEO_LOADED",
        children: /*#__PURE__*/_jsx(WidgetWrapper, {
          children: /*#__PURE__*/_jsx(Video, {
            autoPlay: autoPlay,
            clipRange: clipRange,
            contentId: resolvedContentId,
            creatorHubId: creatorHubId,
            enablePictureInPicture: enablePictureInPicture,
            hublet: creatorHublet,
            isPublic: isPublic,
            language: language || contentLanguage,
            lessonId: resolvedLessonId,
            link: redirectLink,
            mutedByDefault: mutedByDefault,
            onEngagement: onVideoEngagement,
            onFinish: onVideoFinish,
            onPlay: onVideoPlay,
            onProgress: onVideoProgress,
            onWatermarkClick: onWatermarkClick,
            openLinksInNewTab: openLinksInNewTab,
            renderContext: renderContext || 'embedded-academy',
            shortId: shortId,
            startTime: videoStartTime,
            videoType: videoType,
            videoWrapperProps: videoWrapperProps
          })
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    marker: "STATIC_RESULTS_PAGE_ERROR",
    children: /*#__PURE__*/_jsx(NavMarker, {
      name: "STATIC_RESULTS_LOADED",
      children: /*#__PURE__*/_jsx(WidgetWrapper, {
        children: /*#__PURE__*/_jsx(VideoSection, {
          autoPlay: autoPlay,
          clipRange: clipRange,
          contentId: resolvedContentId,
          creatorHubId: creatorHubId,
          enablePictureInPicture: enablePictureInPicture,
          hublet: creatorHublet,
          isPublic: isPublic,
          isResizeReady: isResizeReady,
          language: language || contentLanguage,
          lessonId: resolvedLessonId,
          link: redirectLink,
          linkTextProps: linkTextProps,
          mutedByDefault: mutedByDefault,
          onEngagement: onVideoEngagement,
          onFinish: onVideoFinish,
          onLinkClick: () => {
            usageTracker.track('interaction', Object.assign({
              action: 'static-widget-link-click',
              contentType: isLessonContent ? 'LESSON' : 'SHORT_FORM_CONTENT'
            }, resolvedContentId && {
              contentId: resolvedContentId
            }, resolvedLessonId && {
              lessonId: resolvedLessonId
            }, shortId && {
              shortId
            }));
            Metrics.counter('static-widget-link-to-academy-clicked', {
              variant: variant || 'default'
            }).increment();
          },
          onPlay: onVideoPlay,
          onProgress: onVideoProgress,
          onWatermarkClick: onWatermarkClick,
          openLinksInNewTab: openLinksInNewTab,
          renderContext: renderContext || 'embedded-academy',
          shortId: shortId,
          videoTimestamp: videoStartTime,
          videoType: videoType,
          videoWrapperProps: videoWrapperProps
        })
      })
    })
  });
};